import {
  CircleStackIcon,
  ClipboardDocumentIcon,
  CpuChipIcon,
  ServerStackIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { TechnologyDistributionResourceMeter } from './TechnologyDistributionResourceMeter';
import { HostingChip } from './TechnologyDistributionServerCard';
import {
  DistributionApplicationGroup,
  VisibleAsset,
  bandLabel,
  environmentLabel,
  environmentSummary,
  formatCpu,
  formatGb,
  infrastructureKindLabel,
  productNames,
  worstUtilization,
} from './technologyDistributionView';

const PANEL_ICONS = {
  cpu: CpuChipIcon,
  memory: CircleStackIcon,
  disk: ServerStackIcon,
};

const STATUS_BADGE: Record<string, string> = {
  unknown: 'bg-secondary-100 text-secondary-600',
  low: 'bg-emerald-50 text-emerald-700',
  moderate: 'bg-amber-50 text-amber-800',
  high: 'bg-orange-50 text-orange-700',
  critical: 'bg-rose-50 text-rose-700',
};

type TechnologyDistributionDetailPanelProps = {
  mode: 'server' | 'application';
  server: VisibleAsset | null;
  application: DistributionApplicationGroup | null;
  onClose: () => void;
  onOpenServer: (assetId: string) => void;
};

function copyValue(value: string, success: string): void {
  void navigator.clipboard.writeText(value).then(
    () => toast.success(success),
    () => toast.error('Não foi possível copiar.'),
  );
}

function CopyIconButton({ label, onCopy }: { label: string; onCopy: () => void }) {
  return (
    <button
      type="button"
      aria-label={`Copiar ${label}`}
      title={`Copiar ${label}`}
      onClick={onCopy}
      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-secondary-400 hover:bg-secondary-100 hover:text-secondary-700"
    >
      <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
    </button>
  );
}

export function TechnologyDistributionDetailPanel({
  mode,
  server,
  application,
  onClose,
  onOpenServer,
}: TechnologyDistributionDetailPanelProps) {
  const open = (mode === 'server' && server != null) || (mode === 'application' && application != null);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, open]);

  if (mode === 'server' && server) {
    const environment = environmentSummary(server.hostings);
    const band = worstUtilization(server.asset);
    const vendor = server.asset.vendor?.trim() ?? '';
    const ipAddress = server.asset.ipAddress?.trim() ?? '';
    const summary = [
      infrastructureKindLabel(server.asset.kind),
      environment,
      server.asset.region?.trim(),
    ]
      .filter((part): part is string => Boolean(part))
      .join(' · ');
    return (
      <aside className="rounded-2xl border border-secondary-200 bg-white p-4 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">
              Detalhes do servidor
            </p>
            <div className="mt-1 flex min-w-0 items-center gap-1">
              <h3 className="min-w-0 truncate text-lg font-semibold text-secondary-900">{server.asset.name}</h3>
              <CopyIconButton
                label="nome do servidor"
                onCopy={() => copyValue(server.asset.name, 'Nome do servidor copiado.')}
              />
            </div>
            {summary ? <p className="text-sm text-secondary-500">{summary}</p> : null}
            <div className="mt-2 space-y-1">
              <div className="flex min-w-0 items-center gap-1 text-sm">
                <span className="shrink-0 text-secondary-500">Fornecedor</span>
                <span className="min-w-0 truncate text-secondary-900">{vendor || 'Não informado'}</span>
                {vendor ? (
                  <CopyIconButton
                    label="fornecedor"
                    onCopy={() => copyValue(vendor, 'Fornecedor copiado.')}
                  />
                ) : null}
              </div>
              <div className="flex min-w-0 items-center gap-1 text-sm">
                <span className="shrink-0 text-secondary-500">IP</span>
                <span className="min-w-0 truncate text-secondary-900">{ipAddress || 'Não informado'}</span>
                {ipAddress ? (
                  <CopyIconButton label="IP" onCopy={() => copyValue(ipAddress, 'IP copiado.')} />
                ) : null}
              </div>
            </div>
          </div>
          <CloseDetailButton onClose={onClose} />
        </div>

        <p className={`mt-3 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[band]}`}>
          {bandLabel(band)}
        </p>

        <h4 className="mt-5 text-sm font-semibold text-secondary-800">Capacidade (referência cadastral)</h4>
        <div className="mt-2">
          <TechnologyDistributionResourceMeter asset={server.asset} variant="panel" icons={PANEL_ICONS} />
        </div>
        {server.asset.operatingSystem ? (
          <p className="mt-2 text-xs text-secondary-500">Sistema operacional: {server.asset.operatingSystem}</p>
        ) : null}

        <h4 className="mt-5 text-sm font-semibold text-secondary-800">
          Aplicações ({server.hostings.length})
        </h4>
        {server.hostings.length === 0 ? (
          <p className="mt-2 text-sm text-secondary-500">Nenhuma aplicação hospedada neste ativo.</p>
        ) : (
          <ul className="mt-2 space-y-2">
            {server.hostings.map((hosting) => (
              <li key={hosting.id}>
                <HostingChip hosting={hosting} siblings={server.hostings} />
                {hosting.address ? (
                  <p className="mt-1 px-1 text-xs text-secondary-500">{hosting.address}</p>
                ) : null}
                {productNames(hosting.products) ? (
                  <p className="mt-1 px-1 text-xs text-secondary-500">
                    Produtos: {productNames(hosting.products)}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </aside>
    );
  }

  if (mode === 'application' && application) {
    const serverCount = new Set(application.placements.map((placement) => placement.asset.id)).size;
    const products = productNames(application.products);
    return (
      <aside className="rounded-2xl border border-secondary-200 bg-white p-4 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">
              Detalhes da aplicação
            </p>
            <h3 className="mt-1 truncate text-lg font-semibold text-secondary-900">
              {application.applicationName}
            </h3>
            <p className="mt-1 text-sm text-secondary-500">
              {products || 'Sem produto vinculado'}
            </p>
          </div>
          <CloseDetailButton onClose={onClose} />
        </div>

        <h4 className="mt-5 text-sm font-semibold text-secondary-800">
          Servidores ({serverCount})
        </h4>
        <ul className="mt-2 space-y-2">
          {application.placements.map((placement) => (
            <li key={placement.hosting.id}>
              <button
                type="button"
                onClick={() => onOpenServer(placement.asset.id)}
                className="w-full rounded-xl border border-secondary-200 px-3 py-2 text-left hover:border-primary-300 hover:bg-primary-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium text-secondary-900">
                    {placement.asset.name}
                  </span>
                  <span className="shrink-0 text-xs text-secondary-500">
                    {environmentLabel(placement.hosting.environmentKind)}
                  </span>
                </span>
                <span className="mt-1 block text-xs text-secondary-500">
                  {infrastructureKindLabel(placement.asset.kind)}
                  {placement.hosting.address ? ` · ${placement.hosting.address}` : ''}
                </span>
                <span className="mt-1 block text-xs text-secondary-500">
                  {formatCpu(placement.asset.cpuCores)} · {formatGb(placement.asset.ramGb)} · {formatGb(placement.asset.diskGb)}
                </span>
                <span className="mt-1 block text-xs text-secondary-500">
                  {bandLabel(worstUtilization(placement.asset))}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </aside>
    );
  }

  return null;
}

function CloseDetailButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label="Fechar"
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-secondary-500 transition-colors hover:bg-secondary-100 hover:text-secondary-800"
    >
      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
    </button>
  );
}
